import i18n from '../../../i18n';
import React from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../ui/dialog';
import { Loader2, Save, Trash2, Download, FolderOpen, FileText } from 'lucide-react';
import { format, startOfMonth, endOfMonth, addMonths } from 'date-fns';

// Create Plan Dialog
export const CreatePlanDialog = ({
  open,
  onOpenChange,
  form,
  setForm,
  projects,
  loading,
  onSubmit
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{i18n.t('auto.ctr.createNewProjectPlan')}</DialogTitle>
        <DialogDescription>
          {i18n.t('auto.ctr.createANewManpowerPlanFor')}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label>{i18n.t('auto.ctr.project')}</Label>
          <Select value={form.project_id} onValueChange={(v) => setForm(prev => ({ ...prev, project_id: v  }))}>
            <SelectTrigger>
              <SelectValue placeholder={i18n.t('auto.ctr.selectAProject')} />
            </SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{i18n.t('auto.ctr.planNameOptional')}</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value  }))}
            placeholder="E.g., Phase 1 Installation"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{i18n.t('auto.ctr.startDate2')}</Label>
            <Input
              type="date"
              value={form.start_date}
              onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value  }))}
            />
          </div>
          <div className="space-y-2">
            <Label>{i18n.t('auto.ctr.endDate2')}</Label>
            <Input
              type="date"
              value={form.end_date}
              onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value  }))}
              min={form.start_date}
            />
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">{i18n.t('auto.ctr.cancel')}</Button>
        <Button onClick={onSubmit} disabled={loading || !form.project_id || !form.start_date || !form.end_date} data-testid="dialog-submit-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Create Plan
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Edit Plan Dialog
export const EditPlanDialog = ({
  open,
  onOpenChange,
  plan,
  form,
  setForm,
  loading,
  onSubmit
}) => {
  // Initialize form when dialog opens or plan changes
  React.useEffect(() => {
    if (open && plan) {
      setForm({
        name: plan.name || '',
        start_date: plan.start_date ? plan.start_date.split('T')[0] : '',
        end_date: plan.end_date ? plan.end_date.split('T')[0] : ''
      });
    }
  }, [open, plan, setForm]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{i18n.t('auto.ctr.editPlanDates')}</DialogTitle>
          <DialogDescription>
            {i18n.t('auto.ctr.modifyThePlanNameAndDate')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{i18n.t('auto.ctr.planName')}</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value  }))}
              placeholder="E.g., Phase 1 Installation"
              data-testid="edit-plan-name"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{i18n.t('auto.ctr.startDate2')}</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value  }))}
                data-testid="edit-plan-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>{i18n.t('auto.ctr.endDate2')}</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value  }))}
                min={form.start_date}
                data-testid="edit-plan-end-date"
              />
            </div>
          </div>
          {plan && (
            <div className="bg-gray-50 p-3 rounded-md text-sm text-gray-600">
              <p><strong>{i18n.t('auto.ctr.project2')}</strong> {plan.project_name}</p>
              <p><strong>{i18n.t('auto.ctr.currentDates')}</strong> {plan.start_date?.split('T')[0]} to {plan.end_date?.split('T')[0]}</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">{i18n.t('auto.ctr.cancel')}</Button>
          <Button 
            onClick={onSubmit} 
            disabled={loading || !form.start_date || !form.end_date || form.start_date > form.end_date}
            data-testid="edit-plan-submit"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Add Employee Dialog (Single)
export const AddEmployeeDialog = ({
  open,
  onOpenChange,
  form,
  setForm,
  employees,
  projectRoles,
  existingEmployeeIds,
  onSubmit
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{i18n.t('auto.ctr.addEmployeeToPlan')}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label>{i18n.t('auto.ctr.employee')}</Label>
          <Select value={form.employee_id} onValueChange={(v) => setForm(prev => ({ ...prev, employee_id: v  }))}>
            <SelectTrigger>
              <SelectValue placeholder={i18n.t('auto.ctr.selectAnEmployee')} />
            </SelectTrigger>
            <SelectContent>
              {employees
                .filter(e => (e.status === 'active' || e.is_active !== false) && !existingEmployeeIds.includes(e.id))
                .map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{i18n.t('auto.ctr.roleInProject')}</Label>
          {projectRoles.length > 0 ? (
            <Select value={form.role} onValueChange={(v) => setForm(prev => ({ ...prev, role: v  }))}>
              <SelectTrigger>
                <SelectValue placeholder={i18n.t('auto.ctr.selectARole')} />
              </SelectTrigger>
              <SelectContent>
                {projectRoles.map((role) => (
                  <SelectItem key={role} value={role}>{role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={form.role}
              onChange={(e) => setForm(prev => ({ ...prev, role: e.target.value  }))}
              placeholder="E.g., Team Lead, Technician..."
            />
          )}
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">{i18n.t('auto.ctr.cancel')}</Button>
        <Button onClick={onSubmit} disabled={!form.employee_id} data-testid="dialog-add-to-plan-btn">
          {i18n.t('auto.ctr.addToPlan')}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Bulk Add Employees Dialog
export const BulkAddEmployeeDialog = ({
  open,
  onOpenChange,
  employees,
  projectRoles,
  existingEmployeeIds,
  selectedEmployees,
  setSelectedEmployees,
  bulkRole,
  setBulkRole,
  onSubmit
}) => {
  const availableEmployees = employees.filter(
    e => (e.status === 'active' || e.is_active !== false) && !existingEmployeeIds.includes(e.id)
  );

  const toggleEmployee = (empId) => {
    if (selectedEmployees.includes(empId)) {
      setSelectedEmployees(selectedEmployees.filter(id => id !== empId));
    } else {
      setSelectedEmployees([...selectedEmployees, empId]);
    }
  };

  const selectAll = () => {
    setSelectedEmployees(availableEmployees.map(e => e.id));
  };

  const clearAll = () => {
    setSelectedEmployees([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{i18n.t('auto.ctr.addMultipleEmployeesToPlan')}</DialogTitle>
          <DialogDescription>
            {i18n.t('auto.ctr.selectMultipleEmployeesToAddThem')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Role Selection */}
          <div className="space-y-2">
            <Label>{i18n.t('auto.ctr.roleForAllSelectedEmployees')}</Label>
            {projectRoles.length > 0 ? (
              <Select value={bulkRole} onValueChange={setBulkRole}>
                <SelectTrigger>
                  <SelectValue placeholder={i18n.t('auto.ctr.selectARole')} />
                </SelectTrigger>
                <SelectContent>
                  {projectRoles.map((role) => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={bulkRole}
                onChange={(e) => setBulkRole(e.target.value)}
                placeholder="E.g., Team Lead, Technician..."
              />
            )}
          </div>

          {/* Employee Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Select Employees ({selectedEmployees.length} selected)</Label>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAll} type="button" data-testid="btn-select-all">
                  {i18n.t('auto.ctr.selectAll')}
                </Button>
                <Button variant="outline" size="sm" onClick={clearAll} type="button" data-testid="btn-clear">
                  {i18n.t('auto.ctr.clear')}
                </Button>
              </div>
            </div>
            <div className="border rounded-md max-h-[300px] overflow-y-auto">
              {availableEmployees.length === 0 ? (
                <p className="text-center text-gray-500 py-8">{i18n.t('auto.ctr.allEmployeesAreAlreadyInThe')}</p>
              ) : (
                availableEmployees.map((emp) => (
                  <div
                    key={emp.id}
                    className={`flex items-center gap-3 p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                      selectedEmployees.includes(emp.id) ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => toggleEmployee(emp.id)}
                  >
                    <Checkbox
                      checked={selectedEmployees.includes(emp.id)}
                      onCheckedChange={() => toggleEmployee(emp.id)}
                      data-testid={`bulk-emp-${emp.id}`}
                    />
                    <div className="flex-1">
                      <div className="font-medium">{emp.name}</div>
                      {emp.department && (
                        <div className="text-xs text-gray-500">{emp.department}</div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Summary */}
          {selectedEmployees.length > 0 && bulkRole && (
            <div className="bg-green-50 p-3 rounded-md">
              <p className="text-sm text-green-800">
                Ready to add <strong>{selectedEmployees.length}</strong> employee{selectedEmployees.length > 1 ? 's' : ''} as <strong>{bulkRole}</strong>
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">{i18n.t('auto.ctr.cancel')}</Button>
          <Button 
            onClick={onSubmit} 
            disabled={selectedEmployees.length === 0 || !bulkRole}
            data-testid="bulk-add-submit"
          >
            Add {selectedEmployees.length} Employee{selectedEmployees.length !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Edit Employee Dialog
export const EditEmployeeDialog = ({
  open,
  onOpenChange,
  form,
  setForm,
  employees,
  projectRoles,
  existingEmployeeIds,
  onSubmit
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{i18n.t('auto.ctr.editEmployeeRole')}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label>{i18n.t('auto.ctr.employee')}</Label>
          <Select value={form.employee_id} onValueChange={(v) => setForm(prev => ({ ...prev, employee_id: v  }))}>
            <SelectTrigger>
              <SelectValue placeholder={i18n.t('auto.ctr.selectAnEmployee')} />
            </SelectTrigger>
            <SelectContent>
              {employees
                .filter(e => (e.status === 'active' || e.is_active !== false) && 
                  (e.id === form.original_id || !existingEmployeeIds.includes(e.id)))
                .map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{i18n.t('auto.ctr.roleInProject')}</Label>
          {projectRoles.length > 0 ? (
            <Select value={form.role} onValueChange={(v) => setForm(prev => ({ ...prev, role: v  }))}>
              <SelectTrigger>
                <SelectValue placeholder={i18n.t('auto.ctr.selectARole')} />
              </SelectTrigger>
              <SelectContent>
                {projectRoles.map((role) => (
                  <SelectItem key={role} value={role}>{role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={form.role}
              onChange={(e) => setForm(prev => ({ ...prev, role: e.target.value  }))}
              placeholder="E.g., Team Lead, Technician..."
            />
          )}
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">{i18n.t('auto.ctr.cancel')}</Button>
        <Button onClick={onSubmit} disabled={!form.employee_id} data-testid="dialog-update-btn">
          {i18n.t('auto.ctr.update')}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Custom Holiday Dialog
export const CustomHolidayDialog = ({
  open,
  onOpenChange,
  form,
  setForm,
  onSubmit
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{i18n.t('auto.ctr.addNonWorkingDay')}</DialogTitle>
        <DialogDescription>
          {i18n.t('auto.ctr.markASpecificDateAsNon')}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label>{i18n.t('auto.ctr.date')}</Label>
          <Input
            type="date"
            value={form.date}
            onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value  }))}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">{i18n.t('auto.ctr.cancel')}</Button>
        <Button onClick={onSubmit} disabled={!form.date} data-testid="dialog-add-non-working-day-btn">
          {i18n.t('auto.ctr.addNonWorkingDay')}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Unsaved Changes Confirmation Dialog
export const UnsavedChangesDialog = ({
  open,
  onOpenChange,
  onDiscard,
  onSave
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{i18n.t('auto.ctr.unsavedChanges')}</DialogTitle>
        <DialogDescription>
          {i18n.t('auto.ctr.youHaveUnsavedChangesDoYou')}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">{i18n.t('auto.ctr.cancel')}</Button>
        <Button variant="destructive" onClick={onDiscard} data-testid="dialog-discard-changes-btn">{i18n.t('auto.ctr.discardChanges')}</Button>
        <Button onClick={onSave} data-testid="dialog-save-changes-btn">{i18n.t('auto.ctr.saveChanges')}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Copy to Multiple Weeks Dialog
export const CopyMultiWeeksDialog = ({
  open,
  onOpenChange,
  weekNumber,
  maxWeeks,
  weeksToFill,
  setWeeksToFill,
  onSubmit
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{i18n.t('auto.ctr.copyWeekToMultipleWeeks')}</DialogTitle>
        <DialogDescription>
          Copy all employees&apos; entries from Week {weekNumber} to the following weeks.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label>{i18n.t('auto.ctr.numberOfWeeksToCopyTo')}</Label>
          <Select value={String(weeksToFill)} onValueChange={(v) => setWeeksToFill(parseInt(v))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: maxWeeks }, (_, i) => i + 1).map(n => (
                <SelectItem key={n} value={String(n)}>{n} week{n > 1 ? 's' : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-gray-500">
            This will copy to weeks {weekNumber + 1} through {weekNumber + weeksToFill}
          </p>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">{i18n.t('auto.ctr.cancel')}</Button>
        <Button onClick={onSubmit} data-testid="dialog-submit-btn">Copy to {weeksToFill} Week{weeksToFill > 1 ? 's' : ''}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Export Dialog
export const ExportDialog = ({
  open,
  onOpenChange,
  exportRange,
  setExportRange,
  planStart,
  planEnd,
  loading,
  onSubmit,
  onSubmitPDF
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{i18n.t('auto.ctr.exportProjectPlan')}</DialogTitle>
        <DialogDescription>
          {i18n.t('auto.ctr.exportTheProjectPlanWithWork')}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="exportWholePlan"
            checked={exportRange.exportWholePlan}
            onCheckedChange={(checked) => setExportRange(prev => ({ ...prev, exportWholePlan: checked  }))}
          />
          <Label htmlFor="exportWholePlan">
            Export entire plan ({format(planStart, 'dd/MM/yyyy')} - {format(planEnd, 'dd/MM/yyyy')})
          </Label>
        </div>
        
        {!exportRange.exportWholePlan && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{i18n.t('auto.ctr.startDate2')}</Label>
                <Input
                  type="date"
                  value={exportRange.startDate}
                  onChange={(e) => setExportRange(prev => ({ ...prev, startDate: e.target.value  }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{i18n.t('auto.ctr.endDate2')}</Label>
                <Input
                  type="date"
                  value={exportRange.endDate}
                  onChange={(e) => setExportRange(prev => ({ ...prev, endDate: e.target.value  }))}
                />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => {
                setExportRange({
                  ...exportRange,
                  startDate: format(planStart, 'yyyy-MM-dd'),
                  endDate: format(planEnd, 'yyyy-MM-dd')
                });
              }} data-testid="btn-full-plan">{i18n.t('auto.ctr.fullPlan')}</Button>
              <Button variant="outline" size="sm" onClick={() => {
                const now = new Date();
                setExportRange({
                  ...exportRange,
                  startDate: format(startOfMonth(now), 'yyyy-MM-dd'),
                  endDate: format(endOfMonth(now), 'yyyy-MM-dd')
                });
              }} data-testid="btn-current-month">{i18n.t('auto.ctr.currentMonth')}</Button>
              <Button variant="outline" size="sm" onClick={() => {
                const now = new Date();
                const start = startOfMonth(now);
                setExportRange({
                  ...exportRange,
                  startDate: format(start, 'yyyy-MM-dd'),
                  endDate: format(endOfMonth(addMonths(start, 2)), 'yyyy-MM-dd')
                });
              }} data-testid="btn-next-3-months">{i18n.t('auto.ctr.next3Months')}</Button>
            </div>
          </>
        )}
        
        {exportRange.startDate && exportRange.endDate && (
          <div className="bg-blue-50 p-3 rounded-md">
            <p className="text-sm text-blue-800">
              Export will include data from {exportRange.startDate} to {exportRange.endDate}
            </p>
          </div>
        )}
      </div>
      <DialogFooter className="flex-col sm:flex-row gap-2">
        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">{i18n.t('auto.ctr.cancel')}</Button>
        <Button onClick={onSubmitPDF} disabled={loading} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="export-pdf-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
          Export to PDF
        </Button>
        <Button onClick={onSubmit} disabled={loading} className="bg-green-600 hover:bg-green-700" data-testid="export-excel-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
          Export to Excel
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Save Template Dialog
export const SaveTemplateDialog = ({
  open,
  onOpenChange,
  form,
  setForm,
  employeeOrder,
  defaultWorkHours,
  defaultTravelHours,
  onSubmit
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{i18n.t('auto.ctr.saveAsTemplate')}</DialogTitle>
        <DialogDescription>
          Save the current team configuration as a reusable template.
          This includes employees, their roles, spacers, and default hours settings.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label htmlFor="templateName">{i18n.t('auto.ctr.templateName')}</Label>
          <Input
            id="templateName"
            value={form.name}
            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., Standard Site Team"
            data-testid="template-name-input"
          />
        </div>
        <div>
          <Label htmlFor="templateDesc">{i18n.t('auto.ctr.descriptionOptional')}</Label>
          <Textarea
            id="templateDesc"
            value={form.description}
            onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
            placeholder={i18n.t('auto.ctr.describeWhenToUseThisTemplate')}
            rows={3}
          />
        </div>
        <div className="bg-gray-50 p-3 rounded-md text-sm">
          <p className="font-medium mb-2">{i18n.t('auto.ctr.willBeSaved')}</p>
          <ul className="list-disc list-inside text-gray-600 space-y-1">
            <li>{employeeOrder.filter(e => !e.isSpacer).length} employees with roles</li>
            <li>{employeeOrder.filter(e => e.isSpacer).length} spacer rows</li>
            <li>Default hours: {defaultWorkHours}W / {defaultTravelHours}T</li>
          </ul>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">{i18n.t('auto.ctr.cancel')}</Button>
        <Button onClick={onSubmit} data-testid="save-template-btn">
          <Save className="h-4 w-4 mr-2" /> {i18n.t('auto.ctr.saveTemplate2')}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Load Template Dialog
export const LoadTemplateDialog = ({
  open,
  onOpenChange,
  templates,
  onLoad,
  onDelete
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>{i18n.t('auto.ctr.loadTemplate')}</DialogTitle>
        <DialogDescription>
          {i18n.t('auto.ctr.applyASavedTemplateToThe')}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {templates.length === 0 ? (
          <p className="text-center text-gray-500 py-8">{i18n.t('auto.ctr.noTemplatesSavedYet')}</p>
        ) : (
          templates.map(template => (
            <div key={template.id} className="border rounded-md p-3 hover:bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium">{template.name}</h4>
                  {template.description && (
                    <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                  )}
                  <div className="flex gap-4 mt-2 text-xs text-gray-500">
                    <span>{template.employees?.filter(e => !e.is_spacer).length || 0} employees</span>
                    <span>Default: {template.default_working_hours || 10}W / {template.default_travel_hours || 0}T</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button 
                    size="sm" 
                    onClick={() => onLoad(template)}
                    data-testid={`load-template-${template.id}`}
                  >
                    <FolderOpen className="h-4 w-4 mr-1" /> {i18n.t('auto.ctr.load')}
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => onDelete(template.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-close-btn">{i18n.t('auto.ctr.close')}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// Bulk Hours Assignment Dialog
export const BulkHoursDialog = ({
  open,
  onOpenChange,
  employeeOrder,
  employees,
  planStartDate,
  planEndDate,
  onSubmit
}) => {
  const [selectedEmployees, setSelectedEmployees] = React.useState([]);
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  const [workingHours, setWorkingHours] = React.useState(10);
  const [travelHours, setTravelHours] = React.useState(0);
  const [overwriteMode, setOverwriteMode] = React.useState('overwrite'); // 'overwrite' or 'skip'
  const [includeWeekends, setIncludeWeekends] = React.useState(false);

  // Get actual employees from employeeOrder (filter out spacers)
  const availableEmployees = React.useMemo(() => {
    return employeeOrder
      .filter(e => !e.isSpacer)
      .map(orderItem => {
        // employeeOrder items have 'id' property, not 'employee_id'
        const empId = orderItem.employee_id || orderItem.id;
        const emp = employees.find(e => e.id === empId);
        return emp ? { ...emp, role: orderItem.role } : null;
      })
      .filter(Boolean);
  }, [employeeOrder, employees]);

  const toggleEmployee = (empId) => {
    if (selectedEmployees.includes(empId)) {
      setSelectedEmployees(selectedEmployees.filter(id => id !== empId));
    } else {
      setSelectedEmployees([...selectedEmployees, empId]);
    }
  };

  const selectAll = () => {
    setSelectedEmployees(availableEmployees.map(e => e.id));
  };

  const clearAll = () => {
    setSelectedEmployees([]);
  };

  const handleSubmit = () => {
    onSubmit({
      employeeIds: selectedEmployees,
      startDate,
      endDate,
      workingHours: parseFloat(workingHours) || 0,
      travelHours: parseFloat(travelHours) || 0,
      overwriteMode,
      includeWeekends
    });
    // Reset form
    setSelectedEmployees([]);
    setStartDate('');
    setEndDate('');
    setWorkingHours(10);
    setTravelHours(0);
    setOverwriteMode('overwrite');
    setIncludeWeekends(false);
  };

  // Reset form when dialog closes
  React.useEffect(() => {
    if (!open) {
      setSelectedEmployees([]);
      setStartDate('');
      setEndDate('');
      setWorkingHours(10);
      setTravelHours(0);
      setOverwriteMode('overwrite');
      setIncludeWeekends(false);
    }
  }, [open]);

  const isValid = selectedEmployees.length > 0 && startDate && endDate && startDate <= endDate;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{i18n.t('auto.ctr.bulkHoursAssignment')}</DialogTitle>
          <DialogDescription>
            {i18n.t('auto.ctr.setWorkingHoursAndTravelHours')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Employee Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{i18n.t('auto.ctr.selectEmployees')}</Label>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAll} data-testid="btn-select-all">{i18n.t('auto.ctr.selectAll')}</Button>
                <Button variant="outline" size="sm" onClick={clearAll} data-testid="btn-clear">{i18n.t('auto.ctr.clear')}</Button>
              </div>
            </div>
            <div className="border rounded-md max-h-[150px] overflow-y-auto">
              {availableEmployees.length === 0 ? (
                <p className="text-center text-gray-500 py-4">{i18n.t('auto.ctr.noEmployeesInPlan')}</p>
              ) : (
                availableEmployees.map(emp => (
                  <label
                    key={emp.id}
                    className="flex items-center p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                  >
                    <Checkbox
                      checked={selectedEmployees.includes(emp.id)}
                      onCheckedChange={() => toggleEmployee(emp.id)}
                      data-testid={`bulk-hours-emp-${emp.id}`}
                    />
                    <span className="ml-2 flex-1">{emp.name}</span>
                    {emp.role && <span className="text-xs text-gray-500">{emp.role}</span>}
                  </label>
                ))
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {selectedEmployees.length} of {availableEmployees.length} employees selected
            </p>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{i18n.t('auto.ctr.startDate')}</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                min={planStartDate}
                max={planEndDate}
                data-testid="bulk-hours-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>{i18n.t('auto.ctr.endDate')}</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || planStartDate}
                max={planEndDate}
                data-testid="bulk-hours-end-date"
              />
            </div>
          </div>

          {/* Hours */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{i18n.t('auto.ctr.workingHours')}</Label>
              <Input
                type="number"
                min="0"
                max="24"
                step="0.5"
                value={workingHours}
                onChange={(e) => setWorkingHours(e.target.value)}
                data-testid="bulk-hours-work"
              />
            </div>
            <div className="space-y-2">
              <Label>{i18n.t('auto.ctr.travelHours')}</Label>
              <Input
                type="number"
                min="0"
                max="24"
                step="0.5"
                value={travelHours}
                onChange={(e) => setTravelHours(e.target.value)}
                data-testid="bulk-hours-travel"
              />
            </div>
          </div>

          {/* Options */}
          <div className="space-y-3 pt-2 border-t">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="includeWeekends"
                checked={includeWeekends}
                onCheckedChange={setIncludeWeekends}
                data-testid="bulk-hours-weekends"
              />
              <Label htmlFor="includeWeekends" className="cursor-pointer">
                {i18n.t('auto.ctr.includeWeekendsSaturdaySunday')}
              </Label>
            </div>
            
            <div className="space-y-2">
              <Label>{i18n.t('auto.ctr.whenExistingEntryFound')}</Label>
              <div className="flex gap-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="overwriteMode"
                    value="overwrite"
                    checked={overwriteMode === 'overwrite'}
                    onChange={() => setOverwriteMode('overwrite')}
                    className="mr-2"
                    data-testid="bulk-hours-overwrite"
                  />
                  <span className="text-sm">{i18n.t('auto.ctr.overwriteExistingHours')}</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="overwriteMode"
                    value="skip"
                    checked={overwriteMode === 'skip'}
                    onChange={() => setOverwriteMode('skip')}
                    className="mr-2"
                    data-testid="bulk-hours-skip"
                  />
                  <span className="text-sm">{i18n.t('auto.ctr.skipIfHoursExist')}</span>
                </label>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel-btn">{i18n.t('auto.ctr.cancel')}</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!isValid}
            data-testid="bulk-hours-submit"
          >
            {i18n.t('auto.ctr.applyHours')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
